<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // STYLING
    import 'tailwindcss/tailwind.css';

    // CASSIOPEIA
    import {
        BID_API,
        Heading,
        InputToggle,
        Paragraph,
        SelectShippingOption,
        SESSION_ORGANIZATION,
        Textarea,
    } from '@smatch/cassiopeia-web-components';

    // TYPES
    import type { Auction, Bid, Address, FormComponentLegacy } from 'src/app/types';

    // MODULES
    import AddressSelectOrCreate from 'src/app/routes/addresses/modules/address-select-or-create/AddressSelectOrCreate.svelte';

    /* TYPES ---------------------------------------------------------------------------------- */

    interface CustomSaveEvent extends CustomEvent {
        detail: Address;
    }

    /* PROPS ----------------------------------------------------------------------------------- */

    export let bidKey: string;
    export let auction: Auction | undefined = undefined;
    export let code: Bid['buyer']['shippingOptionCode'] | undefined = undefined;
    export let addressKey: string | undefined = undefined;
    export let logisticalServices: string | undefined = undefined;

    export let hasShipping = typeof addressKey === 'string';
    export let hasServices = typeof logisticalServices === 'string';

    /* INTERNAL -------------------------------------------------------------------------------- */

    let Form: FormComponentLegacy | null = null;

    $: isAddressRequired = code !== undefined && ![ 'exw_ex_works', 'fob_free_on_board' ].includes(code);

    const onToggleInboundAddress = (): void => {
        hasShipping = !hasShipping;
    };

    const onToggleServices = (): void => {
        hasServices = !hasServices;
    };

    const onAddressSave = (event: CustomSaveEvent): void => {
        void BID_API.updateBid({ bidKey }, { buyer: { addressKey: event.detail._.key } });
    };

    /* INTERFACE ------------------------------------------------------------------------------ */

    export const submit = async (): Promise<void> => {
        await (Form as FormComponentLegacy).submit();
    };
    export const isValid = (): boolean => (Form as FormComponentLegacy).isValid();

</script>

<div class = 's8-shipping'>

    <Heading size = 'md'>Logistics Preferences</Heading>

    {#if auction !== undefined }

        {#if auction.shipping?.options !== undefined && auction.shipping.options.length > 1}
            <SelectShippingOption
                bind:key = { code }
                placeholder = 'Select Shipping Option'
                label = 'Shipping Option'
                whitelist = { auction.shipping.options }
            />
        {/if}

        <div class = 's8-section'>
            {#if !isAddressRequired }
                <InputToggle
                    on:change = { onToggleInboundAddress }
                    isChecked = { hasShipping }
                    label = 'Please ship the goods directly to my warehouse'
                    name = 'toggle.address'
                />
            {/if}
        </div>

        {#if hasShipping || isAddressRequired}
            <div class = 's8-section'>
                <AddressSelectOrCreate
                    bind:key = { addressKey }
                    organizationKey = { $SESSION_ORGANIZATION?._.key ?? '' }
                    type = 'inbound'
                    bind:this = { Form }
                    on:save = { onAddressSave }
                />
            </div>
        {/if}

        <div class = 's8-section'>
            <InputToggle
                on:change = { onToggleServices }
                isChecked = { hasServices }
                label = 'I need additional services (such as special packing, custom delivery times ...)'
                name = 'toggle.services'
            />
        </div>

        {#if hasServices}
            <Textarea
                bind:value = { logisticalServices }
                maxLength = { 1000 }
                isRequired = { true }
                placeholder = 'Please describe which services you require ...'
                class = 'min-h-[100px]'
                name = 'bid.services'
                label = { null }
            />

            <Paragraph>We will be in touch to discuss all your logistical needs</Paragraph>
        {/if}

    {/if}

</div>

<style lang="postcss">
    div.s8-shipping {
        div.s8-section {
            margin-top: 10px;

            :global(div.s8-address-item) {
                padding-left: 35px;
            }

            :global(div.s8-buttons) {
                display: none !important;
            }
        }
    }
</style>